import { UserAuthData, Permission, PermissionSchema, Role } from './permissions.types'
import {
  ROLE_PERMISSIONS,
  OPT_IN_PERMISSIONS,
  SUPER_USER_ONLY_PERMISSIONS,
  VIEWER_GRANTABLE_PERMISSIONS,
} from './permissions.constants'

const WILDCARD_PERMISSION = '*' as Permission

/**
 * Get all user permissions (from role + additional)
 * Combines permissions from the user's role with any additional permissions
 * @param user - Authenticated user data
 * @returns Array of permissions
 */
export function getUserPermissions(user: UserAuthData): Permission[] {
  const rolePermissions = ROLE_PERMISSIONS[user.role] || []

  // Super user has all permissions (wildcard)
  if (rolePermissions.includes(WILDCARD_PERMISSION)) {
    return [WILDCARD_PERMISSION]
  }

  // Combine role permissions and additional permissions, removing duplicates
  const additionalPermissions = user.permissions || []
  return [...new Set([...rolePermissions, ...additionalPermissions])]
}

/**
 * Check if a permission is explicitly disabled for the user
 * Disabled permissions override everything, including super_user wildcard
 * @param user - Authenticated user data
 * @param permission - Permission to check
 * @returns true if permission is disabled
 */
export function isPermissionDisabled(user: UserAuthData, permission: Permission): boolean {
  const disabledPermissions = user.disabled_permissions || []
  return disabledPermissions.includes(permission)
}

/**
 * Check if user has specific permission(s)
 * Supports wildcard permissions (e.g., integrations.* covers integrations.google.manage, etc.)
 * Respects disabled_permissions which override everything (even super_user)
 * super_user (*) has ALL permissions; OPT_IN restrictions apply to admin and below
 * @param user - Authenticated user data
 * @param permission - Single permission or array of permissions (ANY match)
 * @returns true if user has at least one of the specified permissions
 */
export function checkPermission(
  user: UserAuthData,
  permission: Permission | Permission[]
): boolean {
  const userPermissions = getUserPermissions(user)
  const permissionsToCheck = Array.isArray(permission) ? permission : [permission]

  // Filter out explicitly disabled permissions first
  const allowedPermissions = permissionsToCheck.filter(perm => !isPermissionDisabled(user, perm))

  // If all permissions are disabled, deny access
  if (allowedPermissions.length === 0) {
    return false
  }

  // Super user (wildcard *) has ALL permissions including opt-in
  // OPT_IN restrictions only apply to admin and below
  if (userPermissions.includes(WILDCARD_PERMISSION)) {
    return true
  }

  return allowedPermissions.some(perm => {
    // Exact match
    if (userPermissions.includes(perm)) return true

    // Opt-in permissions require explicit grant, not covered by resource wildcards
    if (OPT_IN_PERMISSIONS.includes(perm)) return false

    // Super-user-only permissions are never satisfied by resource wildcards.
    // Reaching this branch means the user is not super_user (* short-circuits
    // above) and does not have an explicit additional_permissions grant.
    if (SUPER_USER_ONLY_PERMISSIONS.includes(perm)) return false

    // Check wildcard (e.g., integrations.* covers integrations.google.manage)
    const [resource] = perm.split('.')
    if (userPermissions.includes(`${resource}.*` as Permission)) return true

    return false
  })
}

/**
 * Check if user has ALL specified permissions
 * Unlike checkPermission which checks for ANY, this requires ALL
 * Respects disabled_permissions which override everything (even super_user)
 * @param user - Authenticated user data
 * @param permissions - Array of permissions (ALL must match)
 * @returns true if user has all specified permissions
 */
export function checkAllPermissions(
  user: UserAuthData,
  permissions: Permission[]
): boolean {
  // Check if any of the required permissions is disabled
  if (permissions.some(perm => isPermissionDisabled(user, perm))) {
    return false
  }

  const userPermissions = getUserPermissions(user)

  // Super user (wildcard *) has ALL permissions including opt-in
  if (userPermissions.includes(WILDCARD_PERMISSION)) {
    return true
  }

  return permissions.every(perm => {
    // Exact match
    if (userPermissions.includes(perm)) return true

    // Opt-in permissions require explicit grant, not covered by resource wildcards
    if (OPT_IN_PERMISSIONS.includes(perm)) return false

    // Super-user-only permissions are never satisfied by resource wildcards.
    if (SUPER_USER_ONLY_PERMISSIONS.includes(perm)) return false

    // Check wildcard
    const [resource] = perm.split('.')
    if (userPermissions.includes(`${resource}.*` as Permission)) return true

    return false
  })
}

/**
 * Check if user has specific role(s)
 * @param user - Authenticated user data
 * @param role - Single role or array of roles (ANY match)
 * @returns true if user has one of the specified roles
 */
export function hasRole(user: UserAuthData, role: Role | Role[]): boolean {
  const rolesToCheck = Array.isArray(role) ? role : [role]
  return rolesToCheck.includes(user.role)
}

/**
 * Require permission or throw error
 * Use this in API routes or server actions to enforce permissions
 * @param user - Authenticated user data
 * @param permission - Single permission or array of permissions
 * @throws Error if user doesn't have permission
 */
/**
 * Alias for checkPermission
 * @see checkPermission
 */
export const can = checkPermission

export function requirePermission(
  user: UserAuthData,
  permission: Permission | Permission[]
): void {
  if (!checkPermission(user, permission)) {
    const permStr = Array.isArray(permission) ? permission.join(', ') : permission
    throw new Error(`Permission denied: ${permStr}`)
  }
}

/**
 * Require role or throw error
 * Use this in API routes or server actions to enforce role requirements
 * @param user - Authenticated user data
 * @param role - Single role or array of roles
 * @throws Error if user doesn't have role
 */
export function requireRole(
  user: UserAuthData,
  role: Role | Role[]
): void {
  if (!hasRole(user, role)) {
    const roleStr = Array.isArray(role) ? role.join(', ') : role
    throw new Error(`Role required: ${roleStr}`)
  }
}

/**
 * Get active tenant_id (considering impersonation)
 * Super users can impersonate other tenants, this returns the active tenant
 * @param user - Authenticated user data
 * @returns Active tenant ID (impersonated or original)
 */
export function getActiveTenantId(user: UserAuthData): string {
  return user.impersonated_tenant_id || user.tenant_id
}

/**
 * Check if user is currently impersonating another tenant
 * @param user - Authenticated user data
 * @returns true if user is impersonating
 */
export function isImpersonating(user: UserAuthData): boolean {
  return !!user.impersonated_tenant_id
}

/**
 * Roles allowed to switch tenants (impersonation). Single source of truth for
 * the cross-tenant predicate — gate cross-tenant navigation on this, NOT on
 * `role === 'super_user'`. `integrator` is admin-level but, like super_user,
 * operates across tenants.
 */
export const CROSS_TENANT_ROLES: Role[] = ['super_user', 'integrator']

/**
 * Role-only variant of {@link canImpersonate}, for callers that have just the
 * role string (e.g. refresh-token rotation) and not a full UserAuthData.
 */
export function roleCanImpersonate(role: Role): boolean {
  return CROSS_TENANT_ROLES.includes(role)
}

/**
 * Check if user can impersonate (switch tenants)
 * Allowed for cross-tenant roles (super_user, integrator)
 * @param user - Authenticated user data
 * @returns true if user can impersonate
 */
export function canImpersonate(user: UserAuthData): boolean {
  return roleCanImpersonate(user.role)
}

/**
 * Platform staff: operates across tenants, so may see platform-wide data no
 * single tenant owns (provider health, cross-tenant AI logs). Same role set as
 * {@link CROSS_TENANT_ROLES} but a distinct question — read it as "may see
 * everyone's data", not "may switch tenants".
 *
 * Role-based on purpose. Permissions are the rule elsewhere, but `integrator`
 * is an alias of `ADMIN_PERMISSIONS`, so no permission can separate it from a
 * tenant admin; cross-tenant concerns are exactly the sanctioned exception.
 */
export function isPlatformStaff(user: UserAuthData): boolean {
  return CROSS_TENANT_ROLES.includes(user.role)
}

/**
 * Role hierarchy levels (lower number = higher privilege)
 * integrator sits just below super_user and above admin: super_user manages
 * integrator, integrator manages admin/manager/viewer, and admin cannot manage
 * integrator.
 */
export const ROLE_HIERARCHY: Record<Role, number> = {
  super_user: 1,
  integrator: 2,
  admin: 3,
  manager: 4,
  viewer: 5,
}

/**
 * Check if user's role can manage target role (must be higher in hierarchy)
 * @param userRole - Current user's role
 * @param targetRole - Target user's role
 * @returns true if userRole is higher than targetRole
 */
export function canManageRole(userRole: Role, targetRole: Role): boolean {
  return ROLE_HIERARCHY[userRole] < ROLE_HIERARCHY[targetRole]
}

/**
 * Get all effective permissions for a user (expanded from role + additional - disabled)
 * Returns explicit permission strings, not wildcards
 * Used for permission delegation validation
 * @param user - Authenticated user data
 * @returns Array of effective permission strings
 */
export function getEffectivePermissions(user: UserAuthData): Permission[] {
  const rolePermissions = ROLE_PERMISSIONS[user.role] || []
  const additionalPermissions = user.permissions || []
  const disabledPermissions = user.disabled_permissions || []

  // Super user has all permissions
  if (rolePermissions.includes(WILDCARD_PERMISSION)) {
    // Get all permissions from schema (excluding wildcards)
    const allPerms = PermissionSchema.options as Permission[]
    // Filter out disabled
    return allPerms.filter(p => !disabledPermissions.includes(p))
  }

  // Expand wildcards and combine with additional
  const expandedSet = new Set<Permission>()
  const allPerms = PermissionSchema.options as Permission[]

  // Add role permissions (with wildcard expansion)
  rolePermissions.forEach(perm => {
    const permStr = perm as string
    if (permStr.endsWith('.*')) {
      const resource = permStr.split('.')[0]
      allPerms.filter(p => p.startsWith(`${resource}.`))
        .forEach(p => expandedSet.add(p))
    } else if (allPerms.includes(permStr as Permission)) {
      expandedSet.add(permStr as Permission)
    }
  })

  // Add additional permissions (opt-in)
  additionalPermissions.forEach(perm => {
    if (allPerms.includes(perm)) {
      expandedSet.add(perm)
    }
  })

  // Add viewer-grantable permissions that admin can delegate
  // Admin (and integrator, which is admin-level) can grant these even if not
  // explicitly in their base role
  if (user.role === 'admin' || user.role === 'integrator') {
    VIEWER_GRANTABLE_PERMISSIONS.forEach((perm: Permission) => {
      // Defense-in-depth: never let a super-user-only permission slip into
      // admin's effective surface even if someone mistakenly adds it to
      // VIEWER_GRANTABLE_PERMISSIONS / OPT_IN_PERMISSIONS later.
      if (SUPER_USER_ONLY_PERMISSIONS.includes(perm)) return
      expandedSet.add(perm)
    })
  }

  // Remove disabled permissions
  disabledPermissions.forEach(perm => {
    expandedSet.delete(perm)
  })

  return [...expandedSet]
}

/**
 * Check if user can grant specific permissions to another user
 * User can only grant permissions they themselves have
 * @param grantor - User granting permissions
 * @param permissionsToGrant - Permissions to be granted
 * @returns Object with allowed and denied permissions
 */
export function validatePermissionGrant(
  grantor: UserAuthData,
  permissionsToGrant: Permission[]
): { allowed: Permission[]; denied: Permission[] } {
  const grantorPermissions = getEffectivePermissions(grantor)
  const grantorIsSuperUser = grantor.role === 'super_user'

  const allowed: Permission[] = []
  const denied: Permission[] = []

  permissionsToGrant.forEach(perm => {
    // Super-user-only permissions: only grantable by an actual super_user,
    // regardless of what getEffectivePermissions returned. This guards against
    // future regressions where the permission accidentally appears in an
    // admin-grantable list.
    if (SUPER_USER_ONLY_PERMISSIONS.includes(perm) && !grantorIsSuperUser) {
      denied.push(perm)
      return
    }

    if (grantorPermissions.includes(perm)) {
      allowed.push(perm)
    } else {
      denied.push(perm)
    }
  })

  return { allowed, denied }
}

/**
 * Normalize a raw permission array from the database.
 * Filters out unknown values and logs warnings for invalid entries.
 */
export function normalizePermissionList(raw: unknown, label: string): Permission[] {
  if (!Array.isArray(raw)) {
    if (raw != null) {
      console.warn(`[normalizePermissionList] ${label} is not an array, ignoring`, raw)
    }
    return []
  }

  const valid: Permission[] = []
  const invalid: unknown[] = []

  for (const value of raw) {
    const parsed = PermissionSchema.safeParse(value)
    if (parsed.success) {
      valid.push(parsed.data)
    } else {
      invalid.push(value)
    }
  }

  if (invalid.length > 0) {
    console.warn(`[normalizePermissionList] Ignoring invalid ${label} values:`, invalid)
  }

  return valid
}
